import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/223cawun_1508.png";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, [setIsScrolled]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/90 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="Surfside Patrol"
              className="h-14 w-14 object-contain"
            />
            <div className="ml-3 hidden sm:block">
              <span className="text-[#F5C518] font-bold text-xl tracking-tight font-['Barlow_Condensed']">
                SURFSIDE
              </span>
              <span className="text-white font-bold text-xl tracking-tight font-['Barlow_Condensed'] ml-1">
                PATROL
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm font-semibold uppercase tracking-wider transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-[#F5C518]'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button & Phone */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:714-588-2460"
              className="flex items-center text-zinc-400 hover:text-[#F5C518] transition-colors"
              data-testid="navbar-phone"
            >
              <Phone className="h-4 w-4 mr-2" />
              <span className="text-sm font-medium">714-588-2460</span>
            </a>
            <Link
              to="/contact"
              data-testid="navbar-get-quote"
              className="btn-skew bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300"
            >
              <span>Get Quote</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#121212] border-t border-zinc-800" data-testid="mobile-menu">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className={`block text-lg font-semibold uppercase tracking-wider py-2 ${
                    isActive(link.path) ? 'text-[#F5C518]' : 'text-zinc-400'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:714-588-2460"
                className="flex items-center text-[#F5C518] py-2"
              >
                <Phone className="h-5 w-5 mr-2" />
                <span className="text-lg font-bold">714-588-2460</span>
              </a>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-4 text-center mt-4"
              >
                Get Quote
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
